import { type FormEvent, type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { api, type Billing as BillingData, type BillingDetails, type Checkout } from '../api';
import { Dialog, ErrorNote, formatDay, PageHeader, Status, useToast } from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

/** `₹1,178.82`, or `₹999` when there are no paise. */
export function rupees(paise: number) {
  return `₹${(paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: paise % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

const planName = (plan: string) => plan.charAt(0).toUpperCase() + plan.slice(1);

/* ---------- Razorpay Checkout ---------- */

interface CheckoutResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}

declare global {
  interface Window {
    Razorpay?: new (
      options: Record<string, unknown>,
    ) => { open(): void; on(event: string, cb: (r: unknown) => void): void };
  }
}

let checkoutScript: Promise<void> | null = null;
function loadCheckout() {
  checkoutScript ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => {
      checkoutScript = null;
      reject(new Error('Could not load Razorpay. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });
  return checkoutScript;
}

/** Open Checkout for a subscription. Resolves with the payment, or null if the customer closes it. */
async function pay(checkout: Checkout): Promise<CheckoutResponse | null> {
  await loadCheckout();
  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Could not load Razorpay.');
  return new Promise((resolve) => {
    const rzp = new Razorpay({
      key: checkout.keyId,
      subscription_id: checkout.subscriptionId,
      name: 'Vaartix',
      description: `${checkout.planName} plan, monthly`,
      prefill: checkout.prefill,
      theme: { color: '#1d7fe0' },
      handler: (response: CheckoutResponse) => resolve(response),
      modal: { ondismiss: () => resolve(null) },
    });
    rzp.open();
  });
}

/* ---------- Page ---------- */

export function Billing() {
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const { data, error, reload, setData } = useLoad(() => api<BillingData>('GET', '/billing'), []);
  const [editing, setEditing] = useState<{ next?: string } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const toast = useToast();

  async function choose(plan: string) {
    if (!data?.details) {
      setEditing({ next: plan });
      return;
    }
    setBusy(plan);
    setActionError(null);
    try {
      const checkout = await api<Checkout>('POST', '/billing/subscription', { plan });
      const payment = await pay(checkout);
      if (!payment) return;
      await api('POST', '/billing/subscription/confirm', payment);
      toast.show('Payment received. Activating your plan…');
      // Razorpay tells us by webhook, usually within seconds.
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const next = await api<BillingData>('GET', '/billing');
        setData(next);
        if (next.plan === plan) {
          toast.show(`You're on the ${checkout.planName} plan.`);
          return;
        }
      }
      toast.show('Payment received. Your plan will switch over in a few minutes.');
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  }

  const sub = data?.subscription;
  const monthly = data?.limits.monthlyEmails ?? null;
  const usedShare = data && monthly ? data.usage.sent / monthly : 0;

  return (
    <>
      <PageHeader
        title="Billing"
        description="Your plan, this month's usage and your GST invoices."
      />
      <ErrorNote error={error} />
      {data?.testMode ? (
        <div className="banner">
          <span>
            <strong>Test mode.</strong> Payments go to Razorpay's test environment and no money
            moves. Pay with a Razorpay test card, or the UPI ID <code>success@razorpay</code>.
          </span>
        </div>
      ) : null}
      {data ? (
        <>
          <div className="card stat-card">
            <div className="label">{planName(data.plan)} plan</div>
            <div className="big">
              {data.usage.sent.toLocaleString('en-IN')}{' '}
              <span
                className="dim"
                style={{ fontSize: 18, fontFamily: 'var(--sans)', fontWeight: 400 }}
              >
                {monthly === null
                  ? 'emails this month'
                  : `of ${monthly.toLocaleString('en-IN')} emails this month`}
              </span>
            </div>
            {monthly !== null ? (
              <div className="meter">
                <div
                  className={`fill ${usedShare > 0.9 ? 'warn' : ''}`}
                  style={{ width: `${Math.min(100, usedShare * 100)}%` }}
                />
              </div>
            ) : null}
            <p className="note">
              {sub?.status === 'active' && sub.cancelAtPeriodEnd
                ? `Cancelled: ${planName(sub.plan)} stays active until ${formatDay(sub.currentPeriodEnd)}, then the free plan applies.`
                : sub?.status === 'active'
                  ? `Renews on ${formatDay(sub.currentPeriodEnd)}. `
                  : sub?.status === 'pending'
                    ? 'The last renewal payment failed. Razorpay is retrying it; check your payment method. '
                    : sub?.status === 'authenticated'
                      ? 'Payment received; your plan is being activated. '
                      : null}
              Resets on the 1st of each month (IST). {data.limits.domains}{' '}
              {data.limits.domains === 1 ? 'domain' : 'domains'}, {data.limits.teamMembers} team
              members, {data.limits.retentionDays} days of email logs.
            </p>
            {canEdit &&
            sub &&
            (sub.status === 'active' || sub.status === 'pending') &&
            !sub.cancelAtPeriodEnd ? (
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCancelling(true)}
                >
                  Cancel plan
                </button>
              </div>
            ) : null}
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Plans</h2>
                <p>
                  Billed monthly in rupees. Prices are before 18% GST; every payment gets a GST
                  invoice. See the{' '}
                  <a href="https://vaartix.com/refunds" target="_blank" rel="noreferrer">
                    refund and cancellation policy
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="card-body">
              {!data.enabled ? (
                <p className="muted" style={{ marginTop: 0 }}>
                  Paid plans open after the beta. We'll email you at least 30 days before any price
                  applies to you.
                </p>
              ) : null}
              <div className="plan-grid">
                <PlanCard
                  name="Free"
                  price="₹0"
                  limits={data.free}
                  current={data.plan === 'free'}
                />
                {data.plans.map((p) => (
                  <PlanCard
                    key={p.id}
                    name={p.name}
                    price={rupees(p.pricePaise)}
                    gross={rupees(p.grossPaise)}
                    limits={p}
                    current={data.plan === p.id}
                    action={
                      data.enabled && canEdit && data.plan !== p.id && !isPaid(data) ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={busy !== null}
                          onClick={() => void choose(p.id)}
                        >
                          {busy === p.id ? 'Opening Razorpay…' : `Choose ${p.name}`}
                        </button>
                      ) : null
                    }
                  />
                ))}
              </div>
              {isPaid(data) && data.enabled ? (
                <p className="muted" style={{ marginBottom: 0 }}>
                  To switch plans, cancel this one and choose the new plan when it ends, or write to{' '}
                  <a href="mailto:support@vaartix.com">support@vaartix.com</a> to switch now.
                </p>
              ) : null}
              <ErrorNote error={actionError} />
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Billing details</h2>
                <p>Who the GST invoices are made out to.</p>
              </div>
              {canEdit ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditing({})}
                >
                  {data.details ? 'Edit' : 'Add details'}
                </button>
              ) : null}
            </div>
            <div className="card-body">
              {data.details ? (
                <dl className="facts" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
                  <dt>Name</dt>
                  <dd>{data.details.legalName}</dd>
                  <dt>GSTIN</dt>
                  <dd>{data.details.gstin ?? 'None (invoices without input tax credit)'}</dd>
                  <dt>Address</dt>
                  <dd style={{ whiteSpace: 'pre-line' }}>
                    {data.details.address}
                    {'\n'}
                    {data.states.find((s) => s.code === data.details?.stateCode)?.name}
                  </dd>
                  <dt>Invoices to</dt>
                  <dd>{data.details.email}</dd>
                </dl>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Not added yet. You'll need them before choosing a paid plan.
                </p>
              )}
            </div>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-head">
              <h2>Invoices</h2>
            </div>
            {data.invoices.length ? (
              <div className="table-wrap" style={{ padding: '12px 12px 4px' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th className="hide-sm">Date</th>
                      <th>Plan</th>
                      <th className="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          <Link className="link" to={`/billing/invoices/${inv.id}`}>
                            {inv.number}
                          </Link>
                        </td>
                        <td className="hide-sm muted nowrap">{formatDay(inv.issuedAt)}</td>
                        <td>{planName(inv.plan)}</td>
                        <td className="right nowrap">{rupees(inv.totalPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-body">
                <p className="muted" style={{ margin: 0 }}>
                  No invoices yet.
                </p>
              </div>
            )}
          </div>
        </>
      ) : null}

      {editing && data ? (
        <DetailsDialog
          initial={data.details}
          states={data.states}
          onClose={() => setEditing(null)}
          onSaved={(details) => {
            const next = editing.next;
            setEditing(null);
            setData({ ...data, details });
            toast.show('Billing details saved');
            if (next) void choose(next);
          }}
        />
      ) : null}
      {cancelling && sub ? (
        <Dialog
          title={`Cancel the ${planName(sub.plan)} plan?`}
          description={
            sub.status === 'active'
              ? `You won't be charged again. ${planName(sub.plan)} stays active until ${formatDay(sub.currentPeriodEnd)}; then the free plan's limits apply.`
              : "You won't be charged again, and the free plan's limits apply now."
          }
          onClose={() => setCancelling(false)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setCancelling(false)}>
                Keep plan
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  setCancelling(false);
                  try {
                    await api('POST', '/billing/subscription/cancel');
                    toast.show('Plan cancelled');
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not cancel');
                  }
                }}
              >
                Cancel plan
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}

function isPaid(data: BillingData) {
  return Boolean(data.subscription && data.subscription.status !== 'created');
}

function PlanCard({
  name,
  price,
  gross,
  limits,
  current,
  action,
}: {
  name: string;
  price: string;
  gross?: string;
  limits: BillingData['free'];
  current: boolean;
  action?: ReactNode;
}) {
  return (
    <div className={`plan-option${current ? ' current' : ''}`}>
      <div className="plan-option-head">
        <strong>{name}</strong>
        {current ? <Status status="Current" tone="accent" /> : null}
      </div>
      <div className="plan-price">
        {price}
        <span> / month</span>
      </div>
      <div className="plan-gross">{gross ? `${gross} with GST` : 'No card needed'}</div>
      <ul>
        <li>
          {limits.monthlyEmails === null
            ? 'Custom volume'
            : `${limits.monthlyEmails.toLocaleString('en-IN')} emails a month`}
        </li>
        <li>
          {limits.domains} {limits.domains === 1 ? 'domain' : 'domains'}
        </li>
        <li>{limits.teamMembers} team members</li>
        <li>{limits.retentionDays} days of email logs</li>
      </ul>
      {action}
    </div>
  );
}

const GSTIN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function DetailsDialog({
  initial,
  states,
  onClose,
  onSaved,
}: {
  initial: BillingDetails | null;
  states: BillingData['states'];
  onClose: () => void;
  onSaved: (details: BillingDetails) => void;
}) {
  const { me } = useSession();
  const [form, setForm] = useState({
    legalName: initial?.legalName ?? '',
    gstin: initial?.gstin ?? '',
    address: initial?.address ?? '',
    stateCode: initial?.stateCode ?? '',
    email: initial?.email ?? me.user.email,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));
  const gstin = form.gstin.trim().toUpperCase();

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = await api<BillingDetails>('PUT', '/billing/details', {
        ...form,
        gstin: gstin || null,
      });
      onSaved(saved);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Billing details"
      description="These appear on your GST invoices. Add your GSTIN to claim input tax credit."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="billing-details"
            className="btn btn-primary"
            disabled={busy || !form.legalName.trim() || !form.address.trim() || !form.stateCode}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="billing-details" onSubmit={submit} style={{ display: 'contents' }}>
        <div className="field">
          <label htmlFor="bd-name">Legal name</label>
          <input
            id="bd-name"
            className="input"
            placeholder="Acme Payments Private Limited"
            value={form.legalName}
            onChange={(e) => set('legalName')(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="bd-gstin">GSTIN (optional)</label>
          <input
            id="bd-gstin"
            className="input"
            placeholder="27AAPFU0939F1ZV"
            maxLength={15}
            value={form.gstin}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              set('gstin')(value);
              // The first two digits are the state.
              if (/^\d{2}/.test(value) && states.some((s) => s.code === value.slice(0, 2))) {
                set('stateCode')(value.slice(0, 2));
              }
            }}
          />
          {gstin && !GSTIN.test(gstin) ? (
            <span className="hint warn">A GSTIN has 15 characters, like 27AAPFU0939F1ZV.</span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="bd-address">Billing address</label>
          <textarea
            id="bd-address"
            className="input"
            rows={3}
            placeholder="Street, city and PIN code"
            value={form.address}
            onChange={(e) => set('address')(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="bd-state">State</label>
          <select
            id="bd-state"
            className="input"
            value={form.stateCode}
            onChange={(e) => set('stateCode')(e.target.value)}
          >
            <option value="">Choose a state</option>
            {states.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="bd-email">Send invoices to</label>
          <input
            id="bd-email"
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => set('email')(e.target.value)}
          />
        </div>
        <ErrorNote error={error} />
      </form>
    </Dialog>
  );
}
